import cx from 'classnames';
import PropTypes from 'prop-types';
import { Icon, Message } from '@bwoty-web/ui-kit';
import { ICON_TRAVEL_AIRPLANE } from '@bwoty-web/ui-kit/icons';
import { urlUtils } from '../../../utils/url';
import FlightDetailsHead from '../flightDetailsHead/FlightDetailsHead';
import { getCloudinaryTemplateLogoUrl } from '../../../utils/imageUtils';

import './flightDetails.scss';

const FlightDetails = ({ flights, flightTimeInformation = null, labels, hideFlightTime = false }) => {
  const renderStopOver = (duration, isTechnicalStopOver) => {
    const technicalLayoverLabel = isTechnicalStopOver ? <div>{labels.technicalLayover}</div> : null;

    return (
      <div className="iw-flight-details__stopover-container">
        <p className="iw-flight-details__time">{duration}</p>
        <div className="iw-flight-details__text">
          {labels.layover} {technicalLayoverLabel}
        </div>
      </div>
    );
  };

  const getFlightTime = (flightTimeInfo, legFlightNumber, isTechnicalStopOver) => {
    if (flightTimeInfo == null || isTechnicalStopOver) {
      return null;
    }

    for (let i = 0; i < flightTimeInfo.length; i += 1) {
      if (flightTimeInfo[i].flightNumber === legFlightNumber) {
        return flightTimeInfo[i].timeInAir;
      }
    }

    return null;
  };

  const renderFlightLeg = (leg, index, flightTimeInfo, flight) => {
    let operatingCarrierName = null;
    let arrivalDaysAfterDeparture = null;
    let departureDaysAfterDeparture = null;

    if (leg.operatingCarrierName != null) {
      operatingCarrierName = `(${labels.operatingCarrier} ${leg.operatingCarrierName})`;
    }

    if (leg.arrivalDaysAfterFirstDeparture > 0) {
      arrivalDaysAfterDeparture = ` (+${leg.arrivalDaysAfterFirstDeparture})`;
    }

    if (leg.departureDaysAfterFirstDeparture > 0) {
      departureDaysAfterDeparture = ` (+${leg.departureDaysAfterFirstDeparture})`;
    }

    const carrierImageUrl = getCloudinaryTemplateLogoUrl(flight?.carrierCloudinaryImage, 48);

    return (
      <div key={index}>
        <div className="iw-flight-details__leg-container">
          <div className="iw-flight-details__row">
            <p className="iw-flight-details__time">
              {leg.departureTime}
              {departureDaysAfterDeparture}
            </p>
            <p className="iw-flight-details__text">{leg.departureLocation}</p>
          </div>
          <div className="iw-flight-details__carrier-logo-container">
            {carrierImageUrl ? (
              <img
                src={carrierImageUrl}
                alt={flight.carrierName}
                className="iw-flight-details__carrier-logo"
              />
            ) : (
              <Icon path={ICON_TRAVEL_AIRPLANE} />
            )}
            <div>
              <p className="iw-flight-details__text">
                {getFlightTime(flightTimeInfo, leg.flightNumber, leg.isTechnicalStopOver)}
              </p>
              <p className="iw-flight-details__text">
                {leg.carrierName} {operatingCarrierName}
              </p>
            </div>
          </div>
          <div>
            <div className="iw-flight-details__time">
              {leg.arrivalTime} {arrivalDaysAfterDeparture}
            </div>
            <p className="iw-flight-details__text">{leg.arrivalLocation}</p>
          </div>
        </div>
        {leg.timeOnStopOver ? renderStopOver(leg.timeOnStopOver, leg.isTechnicalStopOver) : null}
      </div>
    );
  };

  const renderFlightDetails = (flight, isHomeBound, flightTimeInfo, index) => {
    const flightClasses = cx('iw-flight-details', {
      'iw-flight-details--homebound': isHomeBound,
      'iw-flight-details--outbound': !isHomeBound,
    });

    return (
      <div
        className={flightClasses}
        key={index}
      >
        <FlightDetailsHead
          flight={flight}
          isHomeBound={isHomeBound}
          labels={labels}
          hideFlightTime={hideFlightTime}
        />
        {flight.legs.map((leg, ix) => renderFlightLeg(leg, ix, flightTimeInfo, flight))}
      </div>
    );
  };

  const numberOfFlights = flights.length;
  return (
    <div className="iw-flight-details-wrapper">
      <div className="iw-flight-details-container">
        {flights.map((flight, index) => {
          const isHomeBound = index === numberOfFlights - 1;
          const flightTime = isHomeBound ? flightTimeInformation?.homeBound : flightTimeInformation?.outBound;
          return renderFlightDetails(flight, isHomeBound, flightTime, index);
        })}
      </div>
      {!urlUtils.isFlightOnly() && (
        <Message
          style={{ marginTop: 'var(--space-600)' }}
          header={labels?.dpPackageFlightHeading}
          messageType="travelInfo"
          expandable
          linkText={labels?.dpTravelConditionsLink?.title}
          link={labels?.dpTravelConditionsLink?.href?.url}
          initiallyExpanded
        >
          {labels?.dpPackageFlightText}
        </Message>
      )}
    </div>
  );
};

FlightDetails.propTypes = {
  labels: PropTypes.shape({
    technicalLayover: PropTypes.string,
    layover: PropTypes.string,
    operatingCarrier: PropTypes.string,
    stopSingular: PropTypes.string,
    stopPlural: PropTypes.string,
    noLayover: PropTypes.string,
    homebound: PropTypes.string,
    outbound: PropTypes.string,
    dpPackageFlightHeading: PropTypes.string,
    dpTravelConditionsLink: PropTypes.shape({
      title: PropTypes.string,
      href: PropTypes.shape({
        url: PropTypes.string,
      }),
    }),
    dpPackageFlightText: PropTypes.string,
  }).isRequired,
  flights: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  flightTimeInformation: PropTypes.shape({
    outBound: PropTypes.arrayOf(PropTypes.shape({})),
    homeBound: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  hideFlightTime: PropTypes.bool,
};

export default FlightDetails;
